import { css, keyframes } from 'styled-components'
import { rem } from 'polished'

const rotateGradient = keyframes`
  from {
    transform: translate(var(--ai-glow-inline-translate), -50%) rotate(0deg);
  }
  to {
    transform: translate(var(--ai-glow-inline-translate), -50%) rotate(360deg);
  }
`

/*
 * A rotating square only covers its host at every angle when its side is at least
 * the host's diagonal, so the layer is grown past the host's own width. 96px is
 * enough slack for every element we glow: they are all wider than 44px and shorter
 * than 100px, and `w + 96 >= sqrt(w² + h²)` holds across that range.
 */
const ROTATION_SLACK_PX = 96

/**
 * The rotating teal conic gradient behind every AI glow treatment, painted on the
 * host's `::before`.
 *
 * Hosts must establish a positioning context, and must either clip the layer
 * (`overflow: hidden`) or mask it down to a band; anything that should read above
 * it needs its own `z-index`.
 */
export const aiGlowRotatingBorderLayer = css`
  --ai-glow-inline-translate: -50%;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    inset-inline-start: 50%;
    width: calc(100% + ${rem(ROTATION_SLACK_PX)});
    aspect-ratio: 1;
    background: conic-gradient(
      from 0deg,
      ${props => props.theme.palette.product.ai.light} 0deg,
      ${props => props.theme.palette.product.ai.dark} 180deg,
      ${props => props.theme.palette.product.ai.light} 360deg
    );
    animation: ${rotateGradient} 2500ms linear infinite;
    z-index: 0;
  }

  [dir='rtl'] & {
    --ai-glow-inline-translate: 50%;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
    }
  }
`

export const AI_GLOW_SUPPRESSED_FILL_CLASS = 'range-glow-suppressed'

/**
 * Falls a day cell back to the plain surface while the glow ring is its only range
 * indicator: the square selection fills would otherwise poke out past the ring's
 * rounded caps. Apply after the cell's selected/in-range rules so it wins over them.
 */
export const aiGlowSuppressedFill = css`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`
